<template>
  <hb-modal
    v-model="dialog"
    size="large"
    title="Change Insurance / Protection Plan Coverage"
    @close="$emit('close')"
    show-help-link
    :size="$vuetify.breakpoint.xs ? 'fullscreen' : ''"
  >
    <template v-slot:subheader>
      <span>
        Send your insurance/protection plan enrollment form to the tenant. Your
        tenant can also sign on this device or a printed copy.
      </span>
    </template>
    <template v-slot:content>
      <hb-form label="Current Coverage">
        <div v-if="isInsuranceDeclined">
          Declined
        </div>
        <div v-else>
          <v-row class="pa-0 ma-0 hb-text-night hb-font-body">
            <v-col cols="12" class="ma-0 pa-0">{{ currentInsurance.Product?.name || currentInsurance.name }}
              <span v-if="currentInsurance.insurance?.coverage" :class="durationPeriodColor" class="hb-font-caption pb-2">({{ currentInsurance.insurance?.coverage }} coverage)</span>
            </v-col>
            <v-col cols="12" class="ma-o pa-0" v-if="currentInsurance.insurance.type == 'library'">
              {{ currentInsurance.insurance.deductible | formatMoney }} Deductible, 
              <span>{{ currentInsurance.insurance.premium_type === '$' ? currentInsurance.insurance.premium_type : '' }}</span>
              <span>{{ currentInsurance.insurance.premium_value | RoundNumber }}</span> 
              Premium
              <span>{{ currentInsurance.insurance.premium_type === '%' ? currentInsurance.insurance.premium_type : '' }}</span>
            </v-col>
          </v-row>
        </div>
      </hb-form>
      <hb-form label="Free Insurance" class="start-insurance" v-if="shouldOfferFreeInsurance && (insurances && insurances.length)">
        <hb-checkbox
          v-model="isFreeInsuranceSelected"
          label="Waive off insurance amount"
        >
        </hb-checkbox>
      </hb-form>
      <hb-form 
        label="Coverage" 
        :description="insurances && insurances.length ? `Select the tenant’s new coverage level.` : '' " 
        class="start-insurance"
      >
        <hb-radio-group v-model="selectedInsurance">
          <hb-radio v-if="!table_rate_insurance" :disabled="isSameInsurance(insurance)" :value="insurance" v-for="insurance in insurances" :key="insurance.id" @change = "checkInsuranceStatus(insurance)">
            <template v-slot:label>
              {{ insurance.name }}
            </template>
            <template v-slot:description v-if="insurance.coverage">
              {{ insurance.deductible | formatMoney }} Deductible, 
              <span>{{ insurance.premium_type === '$' ? insurance.premium_type : '' }}</span>
              <span>{{ insurance.premium | RoundNumber }}</span> 
              Premium
              <span>{{ insurance.premium_type === '%' ? insurance.premium_type : '' }}</span>
            </template>
          </hb-radio>
          <table-rate-insurance
            v-if="table_rate_insurance"
            :insurances="insurances"
            :isSelected="!!selectedInsurance"
            :fullWidth="true"
            :coverageAmount="currentInsurance && currentInsurance.insurance ? currentInsurance.insurance.coverage : null"
            @updateCoverageValue="updateCoverageValue"
          />
        </hb-radio-group>
        <span v-if="!insurances || !insurances.length">
          {{allowOwnCoverage ? "No insurances found" : `${unitType} units are not eligible for coverage` }} 
        </span>
      </hb-form>

      <hb-form label="Decline Coverage" class="start-insurance">
        <hb-checkbox
          v-model="declineInsurance"
          label="Tenant has own coverage"
        >
        </hb-checkbox>

        <div v-if="declineInsurance">
          <div class="mt-1">
            <span>Please select the expiration date of the coverage policy</span>
          </div>
          <v-row class="pa-0 ma-0 mt-5">
            <v-col cols="5" class="pa-0 ma-0">
              <v-select
                :items="CALENDAR.MONTHS_LIST"
                item-text="name"
                hide-details
                v-model="insurance_exp_month"
                clearable
                data-vv-name="insurance_exp_month"
                data-vv-as="Month"
                label="Select Month"
              ></v-select>
            </v-col>

            <v-col cols="5" class="pa-0 ma-0 ml-4">
              <v-select
                :items="CALENDAR.YEARS_LIST"
                item-text="name"
                hide-details
                v-model="insurance_exp_year"
                clearable
                data-vv-name="insurance_exp_year"
                data-vv-as="Year"
                label="Select Year"
              ></v-select>
            </v-col>
          </v-row>
        </div>
      </hb-form>

      <hb-form
        v-if="selectedInsurance"
        class="start-insurance"
        multiple
        label="Start"
        required
      >
        <hb-radio-group v-model="insuranceStart">
          <hb-radio
            :label="`Current Billing Period: ${currentBillingPeriodLabel}`"
            value="current"
          ></hb-radio>
          <hb-radio
            :disabled="isFreeInsuranceSelected"
            :label="`Next Billing Period: ${
              billingPeriods.future &&
              $options.filters.formatDate(
                billingPeriods.future[0].start
              )
            } - ${
              billingPeriods.future &&
              $options.filters.formatDate(billingPeriods.future[0].end)
            }`"
            value="next"
          ></hb-radio>
        </hb-radio-group>
        <div class="mt-3 mb-1">
          <hb-notification
            title-off
            v-model="currentInsuranceCaution"
            v-if="!shouldTakePaymentForCurrentPeriod && isCurrentPeriodSelected && isFreeInsuranceSelected"
            type="success"
            not-dismissable
          >
            The selected insurance is fully paid by the Complimentary Coverage Credit.          
          </hb-notification>
          <hb-notification
            title-off
            v-model="currentInsuranceCaution"
            v-else-if="shouldTakePaymentForCurrentPeriod && isCurrentPeriodSelected && isDowngrade === true && !isNaionalAccount"
            type="caution"
            not-dismissable
          >
          The remaining balance will be applied to any open or future invoices.
          </hb-notification>
          <hb-notification
            title-off
            v-model="currentInsuranceCaution"
            v-if="displayAdjustInvoiceWarning"
            type="caution"
            not-dismissable
          >
            The invoice for next billing period already exists with an insurance. 
            The change will be in effect for any new invoices that would be generated.
            To update insurance in any existing invoices please adjust the invoice.
          </hb-notification>
        </div>
      </hb-form>

      <hb-form label="Cancel From" v-if="currentInsurance && declineInsurance" required>
        <hb-radio-group v-model="cancelForm"
        v-validate.disable="{ required : true }"
        data-vv-name="billing_period"
        data-vv-as="Billing Period"
         data-vv-scope="cancel_form"
           :error-messages="errors.collect('cancel_form')"
        >
        <hb-radio
          :label="`Current Billing Period: ${currentBillingPeriodLabel}`"
            value="current"
          > 
          </hb-radio>
          <hb-radio
          :label="`Next Billing Period: ${
            billingPeriods.future &&
            $options.filters.formatDate(
              billingPeriods.future[0].start
            )
          } - ${
            billingPeriods.future &&
            $options.filters.formatDate(billingPeriods.future[0].end)
          }`"
            value="next"
          >
          </hb-radio>
        </hb-radio-group>
      </hb-form>

      <v-row v-if="displayPaymentModule && isDowngrade === false && !isNaionalAccount && invoice_balance > 0" class="ma-0 pa-5">
        <v-col cols="12" class="ma-0 pa-0">
          <payment-process
            :contactID="contact.id"
            :propertyID="lease.Unit.property_id"
            paymentSource="INSURANCE"
          >
          </payment-process>
        </v-col>
      </v-row>
    </template>
    <template v-slot:actions>
      <hb-btn class="mr-2" v-if="displayPaymentModule && isDowngrade === false && !isNaionalAccount && invoice_balance > 0 && hasPermission('tenant_profile_add_service_skip_payment')" color="secondary" :disabled="(disableInsurance || disablePayment)" :loading="(disableInsurance || disablePayment)" @click="skipPayment">Skip Payment</hb-btn>
      <hb-tooltip v-else-if="!hasPermission('tenant_profile_add_service_skip_payment') && displayPaymentModule">
        <template v-slot:item>
          <hb-btn color="secondary" disabled>Skip Payment</hb-btn>
        </template>
        <template v-slot:body>
          {{ !hasPermission('tenant_profile_add_service_skip_payment') ? 'You do not have the permission to skip payment' : 'You do not have the permission for the payment' }}
        </template>
      </hb-tooltip>

      <hb-btn
        v-if="displayPaymentModule && isDowngrade === false && !isNaionalAccount && invoice_balance > 0"
        color="primary"
        :disabled="disablePayment"
        :loading="disablePayment"
        @click="checkPaymentErrors"
        >Process Payment</hb-btn
      >
      <hb-btn v-else-if="cancelForm && check === 'current'" color="primary" @click="removeInsurance" :disabled="disableInsurance" :loading="disableInsurance"
        >Remove Insurance</hb-btn
      >
      <hb-btn v-else color="primary" @click="saveInsurance" :disabled="disableInsurance" :loading="disableInsurance"
        >Save Insurance</hb-btn
      >
    </template>
  </hb-modal>
</template>

<script type="text/babel">
import moment from 'moment';
import CALENDAR from '@/constants/calendar.js';
import api from "../../../assets/api.js";
import PaymentProcess from "../../includes/PaymentProcess/Index.vue";
import { mapGetters, mapMutations, mapActions } from "vuex";
import { EventBus } from "../../../EventBus.js";
import { notificationMixin } from  '../../../mixins/notificationMixin.js';
import TableRateInsurance from '../../table_rate_insurance/Index.vue';
import axios from 'axios';
import https from 'https';

// Updated component for adding insurance
export default {
  name: "AddNewInsurance",
  mixins: [ notificationMixin ],
  data() {
    return {
      insurances: [],
      selectedInsurance: null,
      insuranceStart: "next",
      currentInsuranceCaution: true,
      disablePayment: false,
      declineInsurance: false,
      isFreeInsuranceSelected: false,
      newInsuranceApplicationDate: null,
      insurance_exp_year: "",
      insurance_exp_month: "",
      disableInsurance: false,
      invoicesToPay: [],
      shouldDisableCurrentPeriod: false,
      shouldOfferFreeInsurance: false,
      shouldTakePaymentForCurrentPeriod: false,
      table_rate_insurance: false,
      coverage_value: "",
	    isUpgrade: false,
      isDowngrade: false,
      cancelForm: '',
      billingPeriods: {},
      check: 'current',
      isNaionalAccount: false,
      updatedBillingLabel: "",
      invoice_balance: 0
    };
  },

  props: {
    lease: {
      type: Object,
      required: true,
    },
    contact: {
      type: Object,
      required: true,
    },
    showAddInsuranceModal: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
    currentInsurance: {
      type: Object,
      default: null
    },
    futureInsurance: {
      type: Object,
      default: null
    },
    declinedInsurance: {
      type: Object,
      default: null
    },
    durationPeriodColor: {
      type: String,
      required: false,
      default: "hb-text-light",
    }
  },

  components: {
    PaymentProcess,
    TableRateInsurance,
  },

  async created() {
    this.CALENDAR = CALENDAR;
    await this.fetchInsurances();
    await this.fetchNextBillingPeriods();
    this.setDefaultInsuranceStart();
    this.table_rate_insurance = this.insurances.length && this.insurances[0]?.insurance_type === "table_rate" ? true : false;
    this.isNaionalAccount = this.contact?.NationalAccount?.id ? true : false;
    EventBus.$on("paymentReader", this.enablePayment);
  },

  destroyed() {
    EventBus.$off("paymentReader", this.enablePayment);
  },

  computed: {
    ...mapGetters({
      getLeases: "paymentsStore/getLeases",
      getErrors: "paymentsStore/getErrors",
      getPaymentInfoFromStore: 'paymentsStore/getPayment', 
    }),
    isInsuranceDeclined() {
      return !this.currentInsurance || !this.currentInsurance.id || this.declinedInsurance.active == 1;
    },
    getInvoice() {
      return {
        selectedInsurance: this.selectedInsurance,
        insuranceStart: this.insuranceStart,
        isFreeInsuranceSelected: this.isFreeInsuranceSelected,
        coverage_value: this.coverage_value,
      };
    },
    displayPaymentModule() {
      return (
        this.shouldTakePaymentForCurrentPeriod &&
        this.selectedInsurance &&
        this.selectedInsurance.id &&
        this.isCurrentPeriodSelected
      );
    },
    shouldFetchInvoice(){
      return (
        ((this.table_rate_insurance && this.coverage_value) || !this.table_rate_insurance) &&
        this.selectedInsurance &&
        this.selectedInsurance.id &&
        this.isCurrentPeriodSelected 
      );
    },
    isCurrentPeriodSelected() {
      return this.insuranceStart === "current";
    },
    displayAdjustInvoiceWarning() {
      if(this.newInsuranceApplicationDate && !this.isCurrentPeriodSelected) {
        return moment(this.newInsuranceApplicationDate) > moment(this.billingPeriods.future[0].start);
      }
    },
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    currentBillingPeriodLabel() {
      const period = this.isFreeInsuranceSelected ? this.billingPeriods?.free_insurance_period : this.billingPeriods?.current;

      const startDate = period && this.$options.filters.formatDate(period.start);
      const endDate = period && this.$options.filters.formatDate(period.end);
      let adjustedStartDate = startDate;
      if ((this.isDowngrade === true && startDate) || this.declineInsurance) {
            adjustedStartDate = moment(startDate).add(1, 'days').format("MMM DD, YYYY");
      }
        return `${adjustedStartDate} - ${endDate}`;
    },
    allowOwnCoverage(){
      return this.template && this.template[this.lease?.unit_id]?.allow_own_coverage == 1;
    },
    unitType(){
      return this.unitTypes.find(u=> u.unit_type_id == this.lease?.Unit?.unit_type_id)?.display_name;
    }
  },

  methods: {
    ...mapMutations({
      createLeaseStructure: "paymentsStore/createLeaseStructure",
      setLeaseBilledMonths: "paymentsStore/setLeaseBilledMonths",
      setCheckErrors: "paymentsStore/setCheckErrors",
    }),
    ...mapActions({
      getTransformedPaymentObject: "paymentsStore/getTransformedPaymentObject",
      setPaymentProcessDisabled: 'paymentsStore/setPaymentProcessDisabled'

    }),
    isSameInsurance(insurance) {
    return (
      this.currentInsurance &&
      insurance.name === this.currentInsurance.name
    );
    },
    checkInsuranceStatus(insurance) {
      this.isUpgrade = false;
      this.isDowngrade = false;

      if (insurance && this.currentInsurance) {
        if (insurance.premium > this.currentInsurance.price) {
          this.isUpgrade = true; 
        } else if (insurance.premium < this.currentInsurance.price) {
          this.isDowngrade = true; 
        }
      }
      if (this.isDowngrade && this.insuranceStart === 'current') {
        this.updatedBillingLabel = this.currentBillingPeriodLabel;
      }   
    },
    setDefaultInsuranceStart() {
      if(this.shouldDisableCurrentPeriod) {
        this.insuranceStart = "next";
      } 
    },
    checkPaymentErrors() {
      this.setCheckErrors({ value: true });
      if(!this.getPaymentInfoFromStore?.type){
            this.showMessageNotification({ type: 'error', title: 'Unable to process payment', description: 'Please select a payment method to continue' });
       }
    },
    async fetchInsurances() {
      try {
        const { id: leaseId } = this.lease;
        const res = await api.get(this, `${api.LEASES}/${leaseId}/insurance`);
        this.insurances = res.data.options;
      } catch (err) {
        console.log("err ", err);
      }
    },
    async fetchPropertyConnections(device_id) {
              let r = await api.get(this.$app, api.PROPERTIES +this.lease.Unit.property_id+ "/connections/address?device_id=" + device_id);
              this.IPaddress = r.IPaddress;
              this.lan = r.lan;
    },
    emitServiceAddedEvents() {
      if(!this.cancelForm) {
        this.showMessageNotification({ type:'success', description: 'Coverage has been changed. View the changes on the tenant profile.' });
      } else {
        this.showMessageNotification({ type:'success', description: 'Coverage has been removed. View the changes on the tenant profile.' });
      }
      EventBus.$emit("closeAddInsuranceModal");
      EventBus.$emit("fetchInsurances");
    },
    async fetchNextBillingPeriods() {
      try {
        const res = await api.get(
          this,
          `${api.LEASES}/${this.lease.id}/insurance-billing-periods?future_months=1`
        );
        this.billingPeriods = res.billing_months;
        this.shouldDisableCurrentPeriod = !res.should_enable_current_period || (this.currentInsurance && this.currentInsurance.id);
        this.shouldOfferFreeInsurance = res.enable_free_insurance;
      } catch (err) {
        console.log("err ", err);
      }
    },
    async saveInsurance(dryRun = false) {
      if (!this.selectedInsurance && !this.declineInsurance) {
        this.showMessageNotification({ description: "Please select an insurance or decline coverage." });
        return;
      }
      let status = true;      
      if(this.declineInsurance) {
        status = await this.$validator.validateAll(
              "cancel_form"
            );
      }

      if(status) {
        this.disableInsurance = true;
        try {
          const insuranceData = {
            decline: false,
            dryrun: dryRun,
            contact_id: this.contact.id,
            property_id: this.lease.Unit.property_id,
            is_free_insurance: this.isFreeInsuranceSelected
          };
          if(this.table_rate_insurance && this.coverage_value) {
            insuranceData.coverage_amount = this.coverage_value;
          }

          if(this.declineInsurance) {
            insuranceData.decline = true;
            insuranceData.insurance_exp_month = this.insurance_exp_month; 
            insuranceData.insurance_exp_year = this.insurance_exp_year;
            return await this.removeInsurance();
          } else {
            insuranceData.insurance_id = this.selectedInsurance.id;
            insuranceData.starting_date = this.insuranceStart === "current" ? moment().format('YYYY-MM-DD')  : this.billingPeriods.future[0].start;
            insuranceData.serviceEnd =this.insuranceStart;
          }

          const response = await this.getOrSaveInsuranceDetails(insuranceData);
          this.invoice_balance = response.Invoice?.balance;
          if (response.error) {
            this.showMessageNotification({ description: response.error });
            return;
          }

          if(!dryRun) {
            this.emitServiceAddedEvents();
          }
          return response; 
        } finally {
          this.disableInsurance = false;
        }
      }
    },

    async removeInsurance(dryRun = false) {
      let status = true;
      if(this.declineInsurance) {
        status = await this.$validator.validateAll(
              "cancel_form"
            );
      }
      console.log('status: ', status);
      if(status) {
        this.disableInsurance = true;
        try {
          const insuranceData = {
            decline: false,
            dryrun: dryRun,
            contact_id: this.contact.id,
            property_id: this.lease.Unit.property_id,
            is_free_insurance: this.isFreeInsuranceSelected
          };
          if(this.declineInsurance) {
          console.log('current --> ', this.currentInsurance);
          
            insuranceData.decline = true;
            insuranceData.insurance_exp_month = this.insurance_exp_month; 
            insuranceData.insurance_exp_year = this.insurance_exp_year;
            if(this.currentInsurance && this.currentInsurance?.id) {
              insuranceData.currentInsuranceId = this.currentInsurance.id;
            }
            console.log(this.check, 'check::');
            
            if(this.check && this.check === 'current') {
              insuranceData.startDate = this.billingPeriods?.current.start;
              insuranceData.endDate = this.billingPeriods?.current.end;
            } else if(this.check && this.check === 'next') {
              insuranceData.startDate = this.billingPeriods?.future[0].start;
              insuranceData.endDate = this.billingPeriods?.future[0].end;
            } else{
              insuranceData.startDate = this.futureInsurance.start_date;
              insuranceData.endDate = this.futureInsurance.start_date;
              this.check = 'current';
            }
            console.log('insurancedata --> ', insuranceData);
            const response = await api.delete(
              this,
              `${api.LEASES}${this.lease.id}/insurance?startDate=${insuranceData.startDate}&period=${this.check}&exp_month=${insuranceData.insurance_exp_month}&exp_year=${insuranceData.insurance_exp_year}`);
            console.log('response --> ', response);
            this.emitServiceAddedEvents();        
            return response;
          }
        } finally {
          this.disableInsurance = false;
        }
      }
      if(!dryRun) {
        this.emitServiceAddedEvents();
      }
      return response; 
    },

    async skipPayment() {
      if (this.hasPermission('tenant_profile_add_service_skip_payment')) {
        try {
          this.disablePayment = true;
          const insuranceData = {
            decline: false,
            dryrun: false,
            contact_id: this.contact.id,
            property_id: this.lease.Unit.property_id,
            is_free_insurance: this.isFreeInsuranceSelected,
            skip_payment: true,
            serviceEnd: this.insuranceStart,
            insurance_id: this.selectedInsurance.id,
          };
          if(this.table_rate_insurance && this.coverage_value) {
            insuranceData.coverage_amount = this.coverage_value;
          }
      
          const response = await this.getOrSaveInsuranceDetails(insuranceData);
          if (response.error) {
            this.showMessageNotification({ description: response.error });
            this.disablePayment = false;
            return;
          }
          this.emitServiceAddedEvents();        
          return response;

        } catch (err) {
          this.showMessageNotification({ description: err })
        } finally {
          this.disablePayment = false;
        }
      } else {
        this.showMessageNotification({ type: 'error', description: 'You do not have the permission to skip payment' });
      }
    },   
    async getOrSaveInsuranceDetails(insuranceData) {
      let response = {};
                let payloadResponse;
                let lanResponseData;
                let payment_id;
                let responseData;

      if(insuranceData?.paymentMethod?.device_id){
        await this.fetchPropertyConnections(insuranceData.paymentMethod.device_id);
      }
      insuranceData.serviceEnd =this.insuranceStart;
       response = {}; 
      try{

        if(insuranceData.paymentMethod && insuranceData.paymentMethod?.device_id && this.lan == 1){
       

       // Create an instance of the HTTPS agent with certificate verification enabled.
               const certData = ``;
       
               const httpsAgent = new https.Agent({
               ca: [certData],
               });
       
               // Configure Axios to use the custom HTTPS agent.
               const instance = axios.create({
               httpsAgent
               });
               const endpoint = 'https://' + this.IPaddress + '?';
               //Get PAX Request Payload from HB Backend server
               payloadResponse = await api.post(this, api.PAYMENTS + "getLanSwipePayload",insuranceData);
               payment_id = payloadResponse.payment.id;
               insuranceData.payment.id = payment_id;
               // Make the GET request using the custom Axios instance.
               lanResponseData = await instance.get(endpoint + payloadResponse.payment.status_desc , {headers: {'Accept': 'text/plain',},});
               insuranceData.payment.PAXResponse = lanResponseData;
               //console.log("lanResponseData :::",lanResponseData);
               this.disable_payment = true;
               response = await api.put(
                  this,
                  `${api.LEASES}/${this.lease.id}/insurance`,
                  insuranceData
                );
        const invoice = response.Invoice;
        this.shouldTakePaymentForCurrentPeriod = !(this.isFreeInsuranceSelected && invoice?.total_payments === (invoice?.sub_total + invoice?.total_tax - invoice?.total_discounts));
          }else{
            response = await api.put(
          this,
          `${api.LEASES}/${this.lease.id}/insurance`,
          insuranceData
        );
        const invoice = response.Invoice;
        this.shouldTakePaymentForCurrentPeriod = !(this.isFreeInsuranceSelected && invoice?.total_payments === (invoice?.sub_total + invoice?.total_tax - invoice?.total_discounts));
        }
        return response;
      }
      catch(error) {
        response = {
            error: error,
            contact_id: insuranceData.contact_id
            };
        if(insuranceData.paymentMethod && insuranceData.paymentMethod?.device_id && this.lan == 1)
          {
            response = await api.post(this, api.PAYMENTS + payment_id + "/lanSwipeResponse", response);
          }
        return {
          error,
          ...response
        };
         
      }
    },
    async payInvoice(data) {
      const response = await this.getOrSaveInsuranceDetails(data);
      return response;
    },
    async fetchInvoice() {
      const response = await this.saveInsurance(true);
      this.invoicesToPay = [response.Invoice];
      this.createLeaseStructure({
        invoices: this.invoicesToPay,
        lease: this.lease,
        unit: this.lease.Unit,
        updateInvoice: this.getLeases && this.getLeases.length,
      });
      this.setLeaseBilledMonths({
        leaseIndex: 0,
        billedMonths: 0,
      });
    },
    async fetchNewInsuranceApplicationDate() {
      try {
        const res = await api.get(this, `${api.LEASES}/${this.lease.id}/insurance-start-date`);
        this.newInsuranceApplicationDate = res;
      } catch (err) {
        console.log("err ", err);
      }
    },
    enablePayment(response) {
      this.disablePayment = false;
      if(response && response.error) {
        this.showMessageNotification({ description: response.error });
      } else if(response && response.data && response.data.msg) {
        this.showMessageNotification({ type: 'success', description: response.data.msg.text });
      }
    },
    async processPayment() {
      this.disablePayment = true;
      this.setPaymentProcessDisabled(true)
      const paymentInfo = await this.getTransformedPaymentObject({
        id: this.$options.name,
        formErrors: this.formErrors,
      });

      if (paymentInfo.errors && paymentInfo.errors.length) {
        this.showMessageNotification({ list: paymentInfo.errors });
        this.disablePayment = false;
        this.setPaymentProcessDisabled(false)
        return;
      }

      const insuranceData = {
        ...paymentInfo,
        decline: false,
        dryrun: false,
        insurance_id: this.selectedInsurance.id,
        is_free_insurance: this.isFreeInsuranceSelected
      };
      if(this.table_rate_insurance && this.coverage_value) {
        insuranceData.coverage_amount = this.coverage_value;
      }
      insuranceData.serviceEnd =this.insuranceStart;

      if (paymentInfo && paymentInfo.payment) {
        try {
          const responseData = await this.getOrSaveInsuranceDetails(insuranceData);      
          if (responseData.error) {
            this.showMessageNotification({ description: responseData.error });
            this.disablePayment = false;
            return;
          }
          EventBus.$emit('lease_edited');
          this.emitServiceAddedEvents();
          if(responseData && responseData.payment_id) {
            EventBus.$emit('showPaymentDone', {payment_id : responseData.payment_id , lease_id : this.lease.id});
          }
        } catch(err){
          this.showMessageNotification({ description: err });
        } finally {
          this.setPaymentProcessDisabled(false)
          this.disablePayment = false;
        }
      }
    },
    updateCoverageValue(coverageValue){
      this.coverage_value = coverageValue;
      this.isUpgrade = false;
      this.isDowngrade = false;
      
      const existingInsuranceCoverage = this.currentInsurance.insurance?.coverage 
      if (this.coverage_value > existingInsuranceCoverage) {
        this.isUpgrade = true;
      } else if (this.coverage_value < existingInsuranceCoverage) {
        this.isDowngrade = true;
      }
      if (this.isDowngrade && this.insuranceStart === 'current') {
        this.updatedBillingLabel = this.currentBillingPeriodLabel; 
      }
    },
  },

  watch: {
    async getInvoice() {
      if (this.shouldFetchInvoice) {
        await this.fetchInvoice();
        EventBus.$emit('handleSourcePropertyUpdate');
      }
    },
    selectedInsurance(insurance) {
      if(insurance && insurance.id) {
        this.declineInsurance = false;
        this.cancelForm = '';
      }
    },
    declineInsurance(check) {
      if(check) {
        this.selectedInsurance = null;
      }
    },
    cancelForm(period) {      
      if(['current', 'next'].includes(period)) {
        this.selectedInsurance = null;
        this.check = period == 'current' ? 'current': 'next';
      }
    },
    async insuranceStart(period) {
      if(!this.newInsuranceApplicationDate && period === 'next') {
        await this.fetchNewInsuranceApplicationDate();
      }
    },
    getErrors(value) {
      if(value === false) {
        this.processPayment();
        this.setCheckErrors({ value: null });
      }
    }
  },
};
</script>

<style>
.start-insurance .v-messages {
  min-height: 0px;
}
</style>